import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSEO } from "@/components/common/SEO";
import { Reveal } from "@/components/common/Reveal";
import { SectionLabel, CtaBanner } from "@/pages/Home";
import api from "@/lib/api";

export default function Projects() {
  const { lang, t, ml } = useI18n();
  const L = (p) => `/${lang}${p}`;
  const [projects, setProjects] = useState([]);
  useSEO({ lang, title: `${t("nav.projects")} | HO-SYS`, description: t("featured.subtitle"), path: "/projects" });

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  return (
    <div className="pt-32">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
        <Reveal>
          <SectionLabel>{t("featured.label")}</SectionLabel>
          <h1 className="mt-5 font-display font-extrabold tracking-tighter text-white text-4xl sm:text-6xl leading-[1.02] max-w-4xl">
            {t("featured.title")}
          </h1>
          <p className="mt-6 text-white/60 text-lg md:text-xl max-w-2xl">{t("featured.subtitle")}</p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {projects.map((p, i) => (
            <Reveal key={p.id} delay={(i % 2) * 0.08}>
              <Link to={L(`/projects/${p.id}`)} data-testid={`projects-grid-${p.id}`}>
                <motion.div whileHover={{ y: -5 }}
                  className="group rounded-2xl overflow-hidden border border-white/10 bg-[#0A0A0A] hover:border-neon/40 transition-colors">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={p.cover} alt={p.name} loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                    <span className="absolute top-4 left-4 rounded-full bg-black/70 backdrop-blur px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-neon border border-neon/30">
                      {ml(p.category)}
                    </span>
                    <span className="absolute top-4 right-4 rounded-full bg-black/70 backdrop-blur px-3 py-1 font-mono text-[11px] text-white/70 border border-white/20">
                      {p.year}
                    </span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="font-display text-2xl font-semibold text-white">{p.name}</h3>
                      <ArrowUpRight className="w-5 h-5 text-white/40 group-hover:text-neon transition-colors" />
                    </div>
                    <p className="mt-2 text-white/50 text-sm">{ml(p.tagline)}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {(p.tech || []).slice(0, 4).map((tech) => (
                        <span key={tech} className="rounded-md bg-white/5 px-2.5 py-1 text-xs text-white/60">{tech}</span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <CtaBanner />
    </div>
  );
}
